import { Route, Routes } from "react-router-dom";
import PublicLayout from "components/Layouts/PublicLayout";
import PrivateLayout from "components/Layouts/PrivateLayout";
import { PermissionRoute } from "components/PermissionRoute";
import { PRIVATE_ROUTES, PUBLIC_ROUTES } from "configs/routes";
import { AuthProvider } from "contexts/auth";
import { RegionProvider_v2 } from "contexts/regions_v2";
import Home from "pages/dashboard/home";
import RequireAuth from "./guards/RequireAuth";
import BffLoginRedirect from "./guards/BffLoginRedirect";
import NotFound from "./NotFound";

/**
 * Central route table.
 *
 * - Public group: rendered inside `PublicLayout`, no `AuthProvider`, no guard —
 *   unauthenticated visitors just see the page.
 * - Private group: wrapped in `AuthProvider > RequireAuth > RegionProvider_v2 >
 *   PrivateLayout`. `RequireAuth` redirects unauthenticated users to the BFF
 *   login; `PermissionRoute` enforces per-route role access.
 *
 * `PRIVATE_ROUTES` / `PUBLIC_ROUTES` come from `configs/routes` and are the
 * shared source of truth for the sidebar/tabs — keep them intact.
 */
export default function AppRoutes() {
  return (
    <Routes>
      {/* --- Public Routes (unguarded) --- */}
      <Route element={<PublicLayout />}>
        <Route path="bff" element={<BffLoginRedirect />} />
        {PUBLIC_ROUTES.map((item) => (
          <Route key={item.path} path={item.path} element={<item.element />} />
        ))}
      </Route>

      {/* --- Private Routes (guarded) --- */}
      <Route
        element={
          <AuthProvider>
            <RequireAuth>
              <RegionProvider_v2>
                <PrivateLayout />
              </RegionProvider_v2>
            </RequireAuth>
          </AuthProvider>
        }
      >
        <Route key="dashboard" path="/dashboard" element={<Home />} />
        {PRIVATE_ROUTES.map((group) =>
          group.children.map((item) => (
            <Route
              key={item.path}
              path={item.path}
              element={
                <PermissionRoute permissions={item.permissions}>
                  <item.element />
                </PermissionRoute>
              }
            />
          ))
        )}
      </Route>

      {/* --- Catch-all 404 (public, unguarded) --- */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
